import React, { useEffect, useRef } from "react";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import {
  looksLikeHtml,
  novedadHtmlBodySx,
  novedadHtmlForDisplay,
  isEmptyNovedadHtml,
  plainTextToHtml,
  sanitizeNovedadHtml,
} from "../../utils/novedadHtml";

function runCommand(command) {
  document.execCommand(command, false, null);
}

function insertSanitizedHtml(html) {
  const safe = sanitizeNovedadHtml(html);
  if (!safe) return;
  const ok = document.execCommand("insertHTML", false, safe);
  if (ok) return;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;
  const range = sel.getRangeAt(0);
  range.deleteContents();
  range.insertNode(range.createContextualFragment(safe));
}

export function NovedadHtmlBody({ html, sx }) {
  const safe = novedadHtmlForDisplay(html);
  if (!safe) return null;
  return (
    <Box
      sx={{ ...novedadHtmlBodySx, ...sx }}
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}

export function NovedadRichTextEditor({
  value,
  onChange,
  placeholder = "Pega aquí el email. Se conserva negrita, listas y títulos.",
  minHeight = 260,
  maxHeight = 480,
  label = "Descripción",
  hint = "Copia el cuerpo del email y pégalo aquí (Ctrl+V). El formato se mantiene.",
  showLabel = true,
  showHint = true,
  ariaLabel,
}) {
  const theme = useTheme();
  const editorRef = useRef(null);
  const skipSync = useRef(false);

  useEffect(() => {
    if (!editorRef.current) return;
    if (skipSync.current) {
      skipSync.current = false;
      return;
    }
    const next = value || "";
    const asHtml = !next
      ? ""
      : looksLikeHtml(next)
        ? next
        : plainTextToHtml(next);
    if (editorRef.current.innerHTML !== asHtml) {
      editorRef.current.innerHTML = asHtml;
    }
  }, [value]);

  const emit = () => {
    if (!editorRef.current) return;
    skipSync.current = true;
    onChange(editorRef.current.innerHTML);
  };

  const handlePaste = (e) => {
    const html = e.clipboardData?.getData("text/html");
    const text = e.clipboardData?.getData("text/plain");
    if (html && html.trim()) {
      e.preventDefault();
      insertSanitizedHtml(looksLikeHtml(text) ? text : html);
      emit();
      return;
    }
    if (text) {
      e.preventDefault();
      insertSanitizedHtml(looksLikeHtml(text) ? text : plainTextToHtml(text));
      emit();
    }
  };

  const handleToolbar = (command) => (e) => {
    e.preventDefault();
    editorRef.current?.focus();
    runCommand(command);
    emit();
  };

  const border = alpha(
    theme.palette.divider,
    theme.palette.mode === "dark" ? 0.9 : 0.72,
  );

  const empty = isEmptyNovedadHtml(value);

  return (
    <Box sx={{ mt: showLabel ? 2 : 0 }}>
      {showLabel && label ? (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mb: 0.75,
            fontWeight: 700,
            color: "text.secondary",
          }}
        >
          {label}
        </Typography>
      ) : null}
      <Box
        sx={{
          border: "1px solid",
          borderColor: border,
          borderRadius: 1.5,
          overflow: "hidden",
          bgcolor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.paper, 0.92)
              : alpha(theme.palette.background.paper, 0.98),
          "&:focus-within": {
            borderColor: theme.palette.primary.main,
          },
        }}
      >
        <Stack
          direction="row"
          spacing={0.25}
          sx={{
            px: 0.5,
            py: 0.25,
            borderBottom: "1px solid",
            borderColor: border,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
          }}
        >
          <Tooltip title="Negrita">
            <IconButton size="small" onMouseDown={handleToolbar("bold")}>
              <FormatBoldIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cursiva">
            <IconButton size="small" onMouseDown={handleToolbar("italic")}>
              <FormatItalicIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Subrayado">
            <IconButton size="small" onMouseDown={handleToolbar("underline")}>
              <FormatUnderlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Lista">
            <IconButton
              size="small"
              onMouseDown={handleToolbar("insertUnorderedList")}
            >
              <FormatListBulletedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Lista numerada">
            <IconButton
              size="small"
              onMouseDown={handleToolbar("insertOrderedList")}
            >
              <FormatListNumberedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Box sx={{ position: "relative" }}>
          {empty ? (
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{
                position: "absolute",
                left: 12,
                top: 10,
                pointerEvents: "none",
                pr: 2,
              }}
            >
              {placeholder}
            </Typography>
          ) : null}
          <Box
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-label={ariaLabel || label || "Editor"}
            onInput={emit}
            onPaste={handlePaste}
            sx={{
              ...novedadHtmlBodySx,
              minHeight,
              maxHeight,
              overflow: "auto",
              px: 1.5,
              py: 1.25,
              outline: "none",
              fontSize: "0.875rem",
            }}
          />
        </Box>
      </Box>
      {showHint && hint ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.75 }}
        >
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
}
